import { Plugin } from 'prosemirror-state';
import { EditorView, NodeView } from 'prosemirror-view';
import { Node as PMNode } from 'prosemirror-model';

/** Narrower than this and the handle is bigger than the image. */
const MIN_WIDTH = 32;

/**
 * Lets an author drag an image to the size they want.
 *
 * The size lives on the node as width/height attributes, so it survives the save and reaches both
 * exports -- the Word converter reads those attributes and otherwise draws every image at the full
 * width of the page.
 *
 * The aspect ratio is taken from the rendered image rather than recomputed from the drag, so an
 * image cannot be squashed: only its width is dragged, and the height follows.
 */
class ResizableImageView implements NodeView {
  public readonly dom: HTMLElement;

  private readonly img: HTMLImageElement;

  private readonly handle: HTMLElement;

  constructor(
    private node: PMNode,
    private readonly view: EditorView,
    private readonly getPos: () => number | undefined,
  ) {
    this.dom = document.createElement('span');
    this.dom.className = 'pm-image';

    this.img = document.createElement('img');
    this.img.src = node.attrs.src as string;
    if (node.attrs.alt) this.img.alt = node.attrs.alt as string;
    if (node.attrs.title) this.img.title = node.attrs.title as string;
    this.applySize(node);

    this.handle = document.createElement('span');
    this.handle.className = 'pm-image-resize-handle';
    // Not focusable and not part of the document: it is a control drawn over the image.
    this.handle.setAttribute('contenteditable', 'false');
    this.handle.addEventListener('mousedown', this.startDrag);

    this.dom.append(this.img, this.handle);
  }

  public update(node: PMNode): boolean {
    if (node.type !== this.node.type || node.attrs.src !== this.node.attrs.src) return false;
    this.node = node;
    this.applySize(node);
    return true;
  }

  public destroy(): void {
    this.handle.removeEventListener('mousedown', this.startDrag);
  }

  /** The handle sits on the image, so a drag there must not also move the caret or the node. */
  public stopEvent(event: Event): boolean {
    return event.target === this.handle;
  }

  private applySize(node: PMNode): void {
    const width = node.attrs.width as number | null;
    const height = node.attrs.height as number | null;
    if (width) this.img.setAttribute('width', String(width)); else this.img.removeAttribute('width');
    if (height) this.img.setAttribute('height', String(height)); else this.img.removeAttribute('height');
  }

  private startDrag = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = this.img.getBoundingClientRect().width;
    // From the rendered image, so the ratio holds even for one that never had explicit dimensions.
    const ratio = this.img.naturalWidth > 0 ? this.img.naturalHeight / this.img.naturalWidth : 0;

    const onMove = (move: MouseEvent) => {
      const width = Math.max(MIN_WIDTH, Math.round(startWidth + (move.clientX - startX)));
      this.img.setAttribute('width', String(width));
      if (ratio) this.img.setAttribute('height', String(Math.round(width * ratio)));
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      this.commit(ratio);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  /**
   * Writes the dragged size back to the document, once, when the drag ends.
   *
   * Deliberately not on every mouse move: each write is an undo step, and a single drag would
   * otherwise take dozens of undos to reverse.
   */
  private commit(ratio: number): void {
    const pos = this.getPos();
    if (pos === undefined) return;

    const width = Math.round(this.img.getBoundingClientRect().width);
    if (width === this.node.attrs.width) return;

    this.view.dispatch(this.view.state.tr.setNodeMarkup(pos, undefined, {
      ...this.node.attrs,
      width,
      ...(ratio && { height: Math.round(width * ratio) }),
    }));
  }
}

export function imageResizePlugin(): Plugin {
  return new Plugin({
    props: {
      nodeViews: {
        image: (node, view, getPos) => new ResizableImageView(node, view, getPos),
      },
    },
  });
}
